import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Loader2, Copy, Mail, MessageCircle } from 'lucide-react';

/**
 * Self-contained modal used by TripFinancials to collect + send a payment link.
 * Props:
 *   - open, onOpenChange
 *   - form, onFormChange
 *   - created (null | {token, whatsappShare, linkId})
 *   - creating (bool)
 *   - onGenerate, onCopy, onEmail, onClose
 *   - tripLabel (string) shown in the header
 */
export default function SendPaymentLinkDialog({
  open, onOpenChange,
  form, onFormChange,
  created, creating,
  onGenerate, onCopy, onEmail, onClose,
  tripLabel,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="trip-send-link-dialog">
        {!created ? (
          <CreateForm
            form={form} onFormChange={onFormChange}
            creating={creating} onGenerate={onGenerate}
            tripLabel={tripLabel}
            onCancel={() => onOpenChange(false)}
          />
        ) : (
          <ShareActions
            created={created} form={form}
            onCopy={onCopy} onEmail={onEmail} onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateForm({ form, onFormChange, creating, onGenerate, onCancel, tripLabel }) {
  const update = (k, v) => onFormChange({ ...form, [k]: v });
  return (
    <>
      <DialogHeader>
        <DialogTitle>Send Payment Link — {tripLabel}</DialogTitle>
        <DialogDescription>Pre-filled from trip details. Customize if needed.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 py-2">
        <div>
          <Label>Amount (INR) *</Label>
          <Input
            data-testid="trip-send-amount"
            type="number" min="1"
            value={form.amount}
            onChange={(e) => update('amount', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Customer Name</Label>
            <Input value={form.customerName} onChange={(e) => update('customerName', e.target.value)} />
          </div>
          <div>
            <Label>Gateway</Label>
            <Select value={form.gateway} onValueChange={(v) => update('gateway', v)}>
              <SelectTrigger data-testid="trip-send-gateway"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cashfree">Cashfree</SelectItem>
                <SelectItem value="razorpay">Razorpay</SelectItem>
                <SelectItem value="stripe">Stripe</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.customerEmail} onChange={(e) => update('customerEmail', e.target.value)} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.customerPhone} onChange={(e) => update('customerPhone', e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Input value={form.description} onChange={(e) => update('description', e.target.value)} />
        </div>
        <div>
          <Label>Expires In (hours)</Label>
          <Input type="number" min="1" max="720" value={form.expiresInHours} onChange={(e) => update('expiresInHours', e.target.value)} />
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={onGenerate}
          disabled={creating}
          className="bg-teal-700 hover:bg-teal-800 text-white"
          data-testid="trip-send-generate-btn"
        >
          {creating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating</> : 'Generate Link'}
        </Button>
      </DialogFooter>
    </>
  );
}

function ShareActions({ created, form, onCopy, onEmail, onClose }) {
  const shareUrl = created.short_url || created.shareUrl
    || `${window.location.origin}/pay/${created.payment_ref || created.token}`;
  return (
    <>
      <DialogHeader>
        <DialogTitle>Payment Link Ready</DialogTitle>
        <DialogDescription>Share with {form.customerName}.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <div className="text-xs text-slate-500 mb-1">Shareable URL</div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={shareUrl}
              className="flex-1 bg-white px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono"
              data-testid="trip-share-url-input"
            />
            <Button size="sm" onClick={onCopy} data-testid="trip-share-copy-btn">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={onEmail} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="trip-share-email-btn">
            <Mail className="w-4 h-4 mr-2" /> Email
          </Button>
          <a href={created.whatsappShare} target="_blank" rel="noreferrer" className="flex-1">
            <Button className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="trip-share-whatsapp-btn">
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
          </a>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </DialogFooter>
    </>
  );
}
